import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

/**
 * Sales Web Portal Redux slice (Phase 0).
 *
 * Holds the governance configuration surface — settings and discount
 * thresholds. Stock, SOA and order state attach here as later phases land.
 */

export interface SalesSetting {
  settingkey: string;
  settingvalue: string;
  description: string | null;
  companyid: number | null;
}

export interface DiscountThreshold {
  thresholdid: number;
  companyid: number | null;
  scopetype: string;
  scopecode: string;
  maxdiscountpct: number;
  isactive: boolean;
}

interface SalesState {
  settings: SalesSetting[];
  thresholds: DiscountThreshold[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SalesState = {
  settings: [],
  thresholds: [],
  isLoading: false,
  error: null,
};

export const fetchSalesSettings = createAsyncThunk(
  'sales/fetchSettings',
  async (companyId: number | undefined, { rejectWithValue }) => {
    try {
      const response: any = await apiService.getSalesSettings(companyId);
      return response.data as SalesSetting[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? 'Failed to load settings');
    }
  }
);

export const fetchSalesThresholds = createAsyncThunk(
  'sales/fetchThresholds',
  async (companyId: number | undefined, { rejectWithValue }) => {
    try {
      const response: any = await apiService.getSalesThresholds(companyId);
      return response.data as DiscountThreshold[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message ?? 'Failed to load thresholds');
    }
  }
);

const salesSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    clearSalesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesSettings.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSalesSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchSalesSettings.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchSalesThresholds.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchSalesThresholds.fulfilled, (state, action) => {
        state.isLoading = false;
        state.thresholds = action.payload;
      })
      .addCase(fetchSalesThresholds.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSalesError } = salesSlice.actions;
export default salesSlice.reducer;
